import React from "react";

type CalendarProps = {
	calendarDate: Date;
	setCalendarDate: (date: Date) => void;
	localDate: Date | null;
	handleDateSelect: (year: number, month: number, day: number) => void;
};

const Calendar: React.FC<CalendarProps> = ({
	calendarDate,
	setCalendarDate,
	localDate,
	handleDateSelect,
}) => {
	return (
		<div className="p-3 space-y-0.5">
			{/* Months */}
			<div className="grid grid-cols-5 items-center gap-x-3 mx-1.5 pb-3">
				{/* Prev Button */}
				<div className="col-span-1">
					<button
						type="button"
						onClick={() => {
							const prevMonth = new Date(calendarDate);
							prevMonth.setMonth(prevMonth.getMonth() - 1);
							setCalendarDate(prevMonth);
						}}
						className="size-8 flex justify-center items-center text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
					>
						<svg
							className="shrink-0 size-4"
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="m15 18-6-6 6-6" />
						</svg>
					</button>
				</div>

				{/* Month / Year */}
				<div className="col-span-3 flex justify-center items-center text-lg font-medium text-gray-900 dark:text-white">
					{calendarDate.getFullYear()}年{calendarDate.getMonth() + 1}月
				</div>

				{/* Next Button */}
				<div className="col-span-1 flex justify-end">
					<button
						type="button"
						onClick={() => {
							const nextMonth = new Date(calendarDate);
							nextMonth.setMonth(nextMonth.getMonth() + 1);
							setCalendarDate(nextMonth);
						}}
						className="size-8 flex justify-center items-center text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
					>
						<svg
							className="shrink-0 size-4"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="m9 18 6-6-6-6" />
						</svg>
					</button>
				</div>
			</div>

			{/* Weekdays */}
			<div className="flex pb-1.5">
				{["月", "火", "水", "木", "金", "土", "日"].map((day) => (
					<span
						key={day}
						className="m-px w-10 block text-center text-sm text-gray-500 dark:text-gray-400"
					>
						{day}
					</span>
				))}
			</div>

			{/* Days */}
			{(() => {
				const year = calendarDate.getFullYear();
				const month = calendarDate.getMonth();

				// 月の最初の日の曜日を取得 (0: 日曜日, 1: 月曜日, ..., 6: 土曜日)
				const firstDay = new Date(year, month, 1).getDay();
				// 月曜日を週の最初にするための調整 (0: 月曜日, 1: 火曜日, ..., 6: 日曜日)
				const firstDayAdjusted = firstDay === 0 ? 6 : firstDay - 1;

				// 月の日数を取得
				const daysInMonth = new Date(year, month + 1, 0).getDate();

				// 前月の日数を取得
				const daysInPrevMonth = new Date(year, month, 0).getDate();

				// 週ごとに日を表示するための配列
				const weeks = [];
				let days = [];

				// 前月の日を追加
				for (let i = firstDayAdjusted - 1; i >= 0; i--) {
					const day = daysInPrevMonth - i;
					days.push(
						<div key={`prev-${day}`}>
							<button
								type="button"
								disabled
								className="m-px size-10 flex justify-center items-center border border-transparent text-sm text-gray-400 rounded-full disabled:opacity-50"
							>
								{day}
							</button>
						</div>
					);
				}

				// 当月の日を追加
				for (let day = 1; day <= daysInMonth; day++) {
					const date = new Date(year, month, day);
					const isToday = date.toDateString() === new Date().toDateString();
					// ローカルの日付オブジェクトを使用して選択されている日を比較
					const isSelected =
						localDate &&
						date.getDate() === localDate.getDate() &&
						date.getMonth() === localDate.getMonth() &&
						date.getFullYear() === localDate.getFullYear();

					days.push(
						<div key={`current-${day}`}>
							<button
								type="button"
								onClick={() => handleDateSelect(year, month, day)}
								className={`m-px size-10 flex justify-center items-center border ${
									isSelected
										? "bg-gradient-to-r from-violet-500 to-purple-600 text-white"
										: isToday
										? "border-violet-600 text-violet-600 dark:border-violet-400 dark:text-violet-400"
										: "border-transparent text-gray-800 dark:text-gray-200 hover:border-violet-600 hover:text-violet-600 dark:hover:border-violet-400 dark:hover:text-violet-400"
								} text-sm rounded-full transition-colors`}
							>
								{day}
							</button>
						</div>
					);

					// 7日ごとに週を区切る
					if ((firstDayAdjusted + day) % 7 === 0 || day === daysInMonth) {
						weeks.push(
							<div key={`week-${weeks.length}`} className="flex">
								{days}
							</div>
						);
						days = [];
					}
				}

				// 翌月の日を追加して最後の週を埋める
				if (days.length > 0) {
					const remainingCells = 7 - days.length;
					for (let day = 1; day <= remainingCells; day++) {
						days.push(
							<div key={`next-${day}`}>
								<button
									type="button"
									disabled
									className="m-px size-10 flex justify-center items-center border border-transparent text-sm text-gray-400 rounded-full disabled:opacity-50"
								>
									{day}
								</button>
							</div>
						);
					}
					weeks.push(
						<div key={`week-${weeks.length}`} className="flex">
							{days}
						</div>
					);
				}

				return weeks;
			})()}
		</div>
	);
};

export default Calendar;
